/**
 * Checkbox Group Component
 *
 * A reusable component to present a list of checkboxes with staged selection
 * and explicit Apply/Cancel actions. Uses the existing Checkbox component and
 * aligns with the UI Core design system (labels, descriptions, buttons, spacing).
 */

import * as React from "react"
import { cn, Button, Checkbox } from "@strivelabs/ui-core"

export interface CheckboxOption {
  /** Unique value for the option */
  value: string
  /** Human-readable label */
  label: string
  /** Optional helper or description text */
  description?: string
  /** Disable a specific option */
  disabled?: boolean
}

export interface CheckboxGroupProps {
  /** Options to render */
  options: CheckboxOption[]
  /** Controlled selected values from the parent */
  selectedValues: string[]
  /** Called when user clicks Apply; receives the staged selections */
  onApply: (values: string[]) => void
  /** Optional callback when user clicks Cancel */
  onCancel?: () => void
  /** Optional callback fired on every staged change (before Apply) */
  onTempChange?: (values: string[]) => void

  /** Label rendered above the group */
  label?: string
  /** Description text under the label */
  description?: string
  /** Disable the whole group */
  disabled?: boolean

  /** Text for action buttons */
  applyButtonText?: string
  cancelButtonText?: string

  /** Custom classes */
  className?: string
  listClassName?: string
  itemClassName?: string
  actionsClassName?: string
  /** Optional name/id for accessibility */
  name?: string
  id?: string
}

const CheckboxGroup = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      options,
      selectedValues,
      onApply,
      onCancel,
      onTempChange,
      label,
      description,
      disabled = false,
      applyButtonText = "Apply",
      cancelButtonText = "Cancel",
      className,
      listClassName,
      itemClassName,
      actionsClassName,
      name,
      id,
      ...props
    },
    ref
  ) => {
    const [staged, setStaged] = React.useState<string[]>(selectedValues || [])

    // Sync staged state when external selection changes
    React.useEffect(() => {
      setStaged(selectedValues || [])
    }, [selectedValues])

    const toggleValue = React.useCallback(
      (value: string) => {
        setStaged((prev) => {
          const exists = prev.includes(value)
          const next = exists ? prev.filter((v) => v !== value) : [...prev, value]
          onTempChange?.(next)
          return next
        })
      },
      [onTempChange]
    )

    const handleApply = React.useCallback(() => {
      onApply(staged)
    }, [onApply, staged])

    const handleCancel = React.useCallback(() => {
      // Just notify cancel without resetting state - parent decides what to do
      onCancel?.()
    }, [onCancel])

    return (
      <div
        ref={ref}
        className={cn(
          "bg-ai-element border-half border-custom-border rounded-lg p-5",
          className
        )}
        {...props}
      >
        {label && (
          <p id={id} className="mb-5 text-base font-medium text-black">
            {label}
          </p>
        )}
        {description && (
          <p className="mb-5 text-sm text-muted-foreground">{description}</p>
        )}

        <div
          role="group"
          aria-labelledby={id}
          className={cn("flex flex-col gap-2", listClassName)}
        >
          {options.map((opt, idx) => {
            const optionId = `${id || name || "checkbox-group"}-${idx}`
            const checked = staged.includes(opt.value)
            return (
              <div
                key={opt.value}
                className={cn(
                  "flex items-center gap-3",
                  itemClassName
                )}
              >
                <Checkbox
                  id={optionId}
                  checked={checked}
                  onCheckedChange={() => toggleValue(opt.value)}
                  disabled={disabled || opt.disabled}
                  className="size-4 text-white data-[state=checked]:bg-custom-text-primary border-custom-border"
                />
                <label htmlFor={optionId} className={cn("cursor-pointer select-none flex-1")}>
                  <div className="text-sm font-normal leading-5 text-custom-text-primary">
                    {opt.label}
                  </div>
                  {opt.description && (
                    <div className="text-sm text-muted-foreground">{opt.description}</div>
                  )}
                </label>
              </div>
            )
          })}
        </div>

        <div className={cn("flex items-center justify-end gap-2 mt-5", actionsClassName)}>
          <Button
            type="button"
            variant="ai-secondary"
            size="xs"
            onClick={handleCancel}
            disabled={disabled}
          >
            {cancelButtonText}
          </Button>
          <Button
            type="button"
            variant="ai-primary"
            size="xs"
            onClick={handleApply}
            disabled={disabled}
          >
            {applyButtonText}
          </Button>
        </div>
      </div>
    )
  }
)

CheckboxGroup.displayName = "CheckboxGroup"

export { CheckboxGroup }
